import {
  forwardRef,
  type CSSProperties,
  type ElementType,
  type MouseEvent,
  type ReactNode,
} from 'react';

import { cn } from '../base/utils';
import { NavigationSidebar } from '../navigation-sidebar';
import { BaseAssetIcon, BaseBadge } from '../base';

export interface WorkbenchResizableSidebarItem {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  iconName?: string;
  count?: number;
  active?: boolean;
  disabled?: boolean;
  href?: string;
  to?: string;
  title?: string;
  endContent?: ReactNode;
  className?: string;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
}

export interface WorkbenchResizableSidebarSection {
  id: string;
  label?: ReactNode;
  items: WorkbenchResizableSidebarItem[];
  className?: string;
  labelClassName?: string;
}

export interface WorkbenchResizableSidebarClassNames {
  root?: string;
  header?: string;
  title?: string;
  collapseButton?: string;
  list?: string;
  section?: string;
  sectionLabel?: string;
  item?: string;
  itemIcon?: string;
  itemLabel?: string;
  resizeHandle?: string;
  mobileNav?: string;
  mobileItem?: string;
}

export interface WorkbenchResizableSidebarProps {
  title?: ReactNode;
  items: WorkbenchResizableSidebarItem[];
  sections?: WorkbenchResizableSidebarSection[];
  activeItemId?: string;
  onItemSelect?: (value: { itemId: string }) => void;
  itemAs?: ElementType;
  collapsed?: boolean;
  defaultCollapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  width?: number;
  onWidthChange?: (width: number) => void;
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  collapsedWidth?: number;
  collapseThreshold?: number;
  storageKey?: string;
  resizable?: boolean;
  collapsible?: boolean;
  collapseLabel?: string;
  expandLabel?: string;
  resizeLabel?: string;
  desktopClassName?: string;
  mobileClassName?: string;
  className?: string;
  classNames?: WorkbenchResizableSidebarClassNames;
  style?: CSSProperties;
}

export const WorkbenchResizableSidebar = forwardRef<HTMLElement, WorkbenchResizableSidebarProps>(
  function WorkbenchResizableSidebar(
    {
      title,
      items,
      sections = [],
      activeItemId,
      onItemSelect,
      itemAs,
      collapsed,
      defaultCollapsed = false,
      onCollapsedChange,
      width,
      onWidthChange,
      defaultWidth = 220,
      minWidth = 180,
      maxWidth = 420,
      collapsedWidth = 60,
      collapseThreshold = 140,
      storageKey,
      resizable = true,
      collapsible = true,
      collapseLabel = 'Collapse sidebar',
      expandLabel = 'Expand sidebar',
      resizeLabel = 'Resize sidebar',
      desktopClassName = 'hidden md:flex',
      mobileClassName = 'flex md:hidden',
      className,
      classNames,
      style,
      ...domProps
    },
    ref,
  ) {
    const project = (item: WorkbenchResizableSidebarItem): WorkbenchResizableSidebarItem => ({
      ...item,
      icon: item.icon ?? (item.iconName ? <BaseAssetIcon icon={{ kind: 'lucide', name: item.iconName }} alt="" className="h-4 w-4 rounded-none bg-transparent" /> : undefined),
      endContent: item.endContent ?? (item.count !== undefined ? <BaseBadge>{item.count}</BaseBadge> : undefined),
      onClick: event => {
        if (item.disabled) return;
        item.onClick?.(event);
        if (!event.defaultPrevented) onItemSelect?.({ itemId: item.id });
      },
    });
    return (
      <NavigationSidebar
        ref={ref}
        kind="catalog"
        title={title}
        items={items.map(project)}
        sections={sections.map(section => ({ ...section, items: section.items.map(project) }))}
        activeItemId={activeItemId}
        itemAs={itemAs}
        collapsed={collapsed}
        defaultCollapsed={defaultCollapsed}
        onCollapsedChange={onCollapsedChange}
        width={width}
        onWidthChange={onWidthChange}
        defaultWidth={defaultWidth}
        minWidth={minWidth}
        maxWidth={maxWidth}
        collapsedWidth={collapsedWidth}
        collapseThreshold={collapseThreshold}
        storageKey={storageKey}
        resizable={resizable}
        collapsible={collapsible}
        collapsePlacement="header"
        collapseLabel={collapseLabel}
        expandLabel={expandLabel}
        resizeLabel={resizeLabel}
        desktopClassName={desktopClassName}
        dataComponent="workbench-resizable-sidebar"
        rootDomProps={{ ...domProps, 'data-workbench-resizable-sidebar': '' }}
        appearance={{
          density: 'compact',
          surface: 'panel',
          activeVariant: 'soft',
        }}
        mobile={{ mode: 'horizontal', className: mobileClassName }}
        className={cn('rounded-xl', className)}
        classNames={{
          root: classNames?.root,
          header: cn('min-h-12 border-0', classNames?.header),
          title: cn('text-[11px] font-medium uppercase text-muted-foreground', classNames?.title),
          collapseButton: classNames?.collapseButton,
          content: cn('space-y-2', classNames?.list),
          section: classNames?.section,
          sectionLabel: classNames?.sectionLabel,
          item: cn('h-9 min-h-9 rounded-md px-2 text-[12px] shadow-none', classNames?.item),
          itemIcon: cn('[&_svg]:h-3.5 [&_svg]:w-3.5', classNames?.itemIcon),
          itemLabel: classNames?.itemLabel,
          resizeHandle: classNames?.resizeHandle,
          mobileNav: classNames?.mobileNav,
          mobileItem: cn('h-9 min-h-9 shrink-0 px-3 text-xs', classNames?.mobileItem),
        }}
        style={style}
      />
    );
  },
);
